import { defineStore } from 'pinia';
import { MeteringPointDirection, GenerationConfiguration, IdentificationType, MemberType } from '../../types/src/enums';
import type {
  IMeteringPointFormData,
  IMemberApplicationFormData,
  IMemberPersonalFormData,
  IMemberBillingFormData
} from '../../types/src/forms';
import type {
  IAddressPostal,
  IContactData,
  IWireInfo
} from '../../types/src/schemas';
import i18n from '@/plugins/i18n';
import type { IAgreement, IAgreementForm } from '../../types/src/schemas';

// TODO esb-452: Global plan for aligning form field names with shared types:
// 1. Update component bindings to use the new field names
// 2. Rename the properties in the store state
// 3. Remove the Omit<> extensions and use the shared types directly
// 4. Update the conversion methods to handle direct property access

/**
 * Extends IAddressPostal for legacy form compatibility
 * TODO esb-452: Rename these properties to match IAddressPostal:
 * - address → street
 * - addressExtra → additional
 * - Consider if 'name' field is still needed or can be moved elsewhere
 */
interface FormAddressPostal extends Omit<IAddressPostal, 'street' | 'streetNumber'> {
  // Legacy form field names
  name: string; // Extra field in form
  address: string; // Maps to 'street' in IAddressPostal
  addressExtra: string; // Maps to 'additional' in IAddressPostal
}

/**
 * Extends IContactData for legacy form compatibility
 * TODO esb-452: Rename these properties to match IContactData:
 * - emailAddress → email
 * - nameFirst → firstName
 * - nameLast → lastName
 * - phoneNumber → phone
 * - Consider if extra fields should be moved to a separate interface
 */
interface FormContactData extends Omit<IContactData, 'email' | 'firstName' | 'lastName' | 'phone'> {
  // Legacy form field names
  emailAddress: string; // Maps to 'email' in IContactData
  nameFirst: string; // Maps to 'firstName' in IContactData
  nameLast: string; // Maps to 'lastName' in IContactData
  phoneNumber: string; // Maps to 'phone' in IContactData
  nameFriendly: string; // Extra field in form
  dateOfBirth: string; // Extra field in form
  companyName: string; // Extra field in form
  salutation?: string; // Extra field for salutation
  titleBefore?: string; // Extra field for title before name
  titleAfter?: string; // Extra field for title after name
}

/**
 * Extends IWireInfo for legacy form compatibility
 * TODO esb-452: Add support for 'reference' field from IWireInfo
 */
type FormWireInfo = Omit<IWireInfo, 'reference'>

/**
 * Custom tax info structure
 * TODO esb-452: Consider moving these fields directly into FormBillingData
 * to match IMemberBillingFormData structure (vatId, vatExempt)
 */
interface FormTaxInfo {
  taxSelection: string; // Maps to vatExempt in IMemberBillingFormData
  taxRatePercentPoints: number;
  taxNote: string;
  uid: string; // Maps to vatId in IMemberBillingFormData
}

/**
 * Custom billing data structure that adapts to IMemberBillingFormData
 * TODO esb-452: Refactor to match IMemberBillingFormData structure:
 * - Move tax properties up to this level (vatId, vatExempt)
 * - Consider using IdentificationType enum for identificationType property
 */
interface FormBillingData {
  addressPostal: FormAddressPostal;
  identification: string;
  identificationType: string; // Should be IdentificationType enum
  taxInfo: FormTaxInfo;
  wireInfo: FormWireInfo;
}

/**
 * Structure for consent objects used in the form
 */
interface FormConsent {
  id: string;
  description: string;
  required: boolean;
  link?: {
    text: string;
    url: string;
  };
}

/**
 * Form metering point structure that adapts to IMeteringPointFormData
 * TODO esb-452: Refactor to have numeric types for numeric values (with string conversions handled in UI):
 * TODO export all these interfaces + put them in own file
 * - assumptionEnergyPerYearKwh: number instead of string
 * - energyStorageKwh: number instead of string
 * - lineCapacity: number instead of string
 */
interface FormMeteringPoint {
  nameFriendly: string;
  meteringPoint: string;
  meteringPointDirection: string;
  ecnDso?: string; // Extra field for CC / 16b
  participationFactor?: number; // Participation factor as percentage string
  addressPostal: FormAddressPostal;
  assumptionEnergyPerYearKwh: string; // String for form input
  energyStorageKwh: string; // String for form input
  plant: {
    generationConfiguration: string;
    lineCapacity: string; // String for form input
    generationType: string;
  };
}

/**
 * Extension of IMemberApplicationFormData with UI-specific fields
 * TODO esb-452: Refactor to more closely match IMemberApplicationFormData structure:
 * - Move UI-tracking fields to a separate interface
 * - Rename properties to match shared types
 * - Use proper typing for enums and boolean fields
 */
interface FormMemberApplication {
  // UI-specific tracking fields
  meteringPointActive2: boolean;
  meteringPointActive3: boolean;

  // Fields that map to IMemberApplicationFormData
  agreedJoin: boolean;
  agreedDataProcessing: boolean;
  agreedNewsletterConsent: boolean;
  agreedSepa?: boolean;
  notes: string;
  isCompany: MemberType | string; // Extra field for UI - using MemberType enum
  // Nested structures with legacy field names
  meteringPointsAssociated: FormMeteringPoint[];
  legal: string; // Used in both personal and billing data
  confirmedSmeState: boolean | null;
  contactData: FormContactData;
  billingData: FormBillingData;
  // Dynamic custom agreement properties
  [key: string]: any;
}

/**
 * Form data state using our extended types
 */
interface FormDataState {
  communityMember: FormMemberApplication;
  // Array of consent objects for frontend display
  customAgreements: IAgreementForm[];
}

export const useFormDataStore = defineStore('formDataStore', {
  persist: {
    storage: localStorage,
    key: "formDataStoreV2" // increase if data changes below
  },

  state: (): FormDataState => ({
    // TODO: ESB-462: Load these from API
    customAgreements: [
      {
        id: 'agreedJoin',
        description: '', // To be filled dynamically
        required: true
      },
      {
        id: 'agreedDataProcessing',
        description: '', // To be filled dynamically
        required: true,
        link: {
          text: 'Datenschutzerklärung',
          url: 'https://www.so-strom.at/datenschutz/'
        }
      }
    ],
    communityMember: {
      // track selection
      meteringPointActive2: false,
      meteringPointActive3: false,

      meteringPointsAssociated: [
        { // Default first metering point
          nameFriendly: "",
          meteringPoint: "",
          meteringPointDirection: "",
          ecnDso: "", // Initialize as empty string, will be set by user selection
          participationFactor: 100, // Default participation factor
          addressPostal: {
            name: "", // skip
            address: "",
            addressExtra: "",
            zip: "",
            city: "",
            country: "AT", // Default value
          },
          assumptionEnergyPerYearKwh: "",
          energyStorageKwh: "",
          plant: {
            generationConfiguration: "",
            lineCapacity: "",
            generationType: "",
          },
        },
      ],

      legal: "",
      confirmedSmeState: null,

      billingData: {
        addressPostal: {
          name: "", // fill
          address: "",
          addressExtra: "",
          zip: "",
          city: "",
          country: "AT", // Default value
        },
        identification: "",
        identificationType: "", // fill

        taxInfo: {
          taxSelection: "",
          taxRatePercentPoints: 0,
          taxNote: "",
          uid: "",
        },
        wireInfo: {
          accountHolder: "",
          iban: "",
          bic: "",
        },
      },

      contactData: {
        nameFriendly: "", // fill
        emailAddress: "",
        phoneNumber: "+43",
        nameFirst: "",
        nameLast: "",
        dateOfBirth: "",
        companyName: "",
        preferredContactMethod: undefined, // Added from IContactData
        salutation: "", // Added for salutation field
        titleBefore: "", // Added for title before name
        titleAfter: "", // Added for title after name
      },

      // to load into others
      isCompany: "",
      notes: "",
      agreedJoin: false,
      agreedDataProcessing: false,
      agreedNewsletterConsent: false,
    },
  }), // state

  getters: {
    formData(state) {
      return state.communityMember;
    }
  }, // getters

  actions: {
    /**
     * Get dynamically generated consent texts and custom agreements based on community
     * @param communityName - Name of the community
     * @param communityLegalType - Legal form of the community
     * @param communityType - Type of community
     * @param customAgreements - Array of custom agreements from community data
     * @returns Array of consent objects with descriptions
     */
    getCustomAgreements(communityName: string, communityLegalName: string, communityLegalType: string, communityType: string, customAgreements: IAgreement[] = [], needsSepaUpfront = false, sepaAgreement: IAgreement | null = null) {
      const isNoMemberJoin = communityType === '16a';
      const t = i18n.global.t;

      // Update standard consent descriptions based on community data
      this.customAgreements[0].description = isNoMemberJoin
        ? t('formAgreements.participateInCommunity', { communityName, communityLegalName, communityLegalType })
        : t('formAgreements.joinCommunity', { communityName, communityLegalName, communityLegalType });

      this.customAgreements[1].description = t('formAgreements.agreeDataProcessing', { communityLegalName });

      // Add custom agreements from community data if they exist
      const allAgreements = [...this.customAgreements];

      // Add newsletter consent as optional checkbox
      if (this.communityMember.agreedNewsletterConsent === undefined) {
        this.communityMember.agreedNewsletterConsent = false;
      }

      allAgreements.push({
        id: 'agreedNewsletterConsent',
        description: t('subscribeToSostromNewsLetter'),
        required: false // Newsletter consent is optional
      });

      if (customAgreements && customAgreements.length > 0) {
        customAgreements.forEach((agreement, index) => {
          const customId = `customAgreement${index}`;

          // Initialize the custom agreement property in the form data if it doesn't exist
          if (!(customId in this.communityMember)) {
            this.communityMember[customId] = false;
          }

          allAgreements.push({
            id: customId,
            description: agreement.description,
            required: true,
            link: agreement.link ? {
              text: t('formAgreements.moreInformation'),
              url: agreement.link
            } : undefined
          });
        });
      }

    if (needsSepaUpfront && sepaAgreement) {
        if (!('agreedSepa' in this.communityMember)) {
            this.communityMember['agreedSepa'] = false;
        }

        allAgreements.push({
            id: 'agreedSepa',
            description: sepaAgreement.description,
            required: true,
            link: sepaAgreement.link ? {
                text: t('formAgreements.moreInformation'),
                url: sepaAgreement.link
            } : undefined
        });
    }

      return allAgreements;
    },

    /**
     * Reset custom agreement states when switching communities
     */
    resetCustomAgreements() {
      // Remove any existing custom agreement properties
      Object.keys(this.communityMember).forEach(key => {
        if (key.startsWith('customAgreement')) {
          delete this.communityMember[key];
        }
      });
        if ('agreedSepa' in this.communityMember) {
            delete this.communityMember['agreedSepa'];
        }
        // Reset newsletter consent but keep the property
        this.communityMember.agreedNewsletterConsent = false;
    },

    /**
     * Convert from our form address format to the shared IAddressPostal format
     * TODO esb-452: After field renaming, this can be simplified or eliminated
     */
    convertAddressPostal(address: FormAddressPostal): IAddressPostal {
      return {
        street: address.address,
        streetNumber: "", // TODO esb-452: Split address into street and number
        additional: address.addressExtra,
        zip: address.zip,
        city: address.city,
        country: address.country || "AT"
      };
    },

    /**
     * Convert from our form contact data to the shared IContactData format
     * TODO esb-452: After field renaming, this can be simplified or eliminated
     */
    convertContactData(contact: FormContactData): IContactData {
      return {
        firstName: contact.nameFirst,
        lastName: contact.nameLast,
        email: contact.emailAddress, // TODO esb-452: Rename to match in both places
        phone: contact.phoneNumber, // TODO esb-452: Rename to match in both places
        preferredContactMethod: contact.preferredContactMethod
      };
    },

    /**
     * Convert from our form wire info to the shared IWireInfo format
     * TODO esb-452: After adding reference field, this can be simplified or eliminated
     */
    convertWireInfo(wireInfo: FormWireInfo): IWireInfo {
      return {
        iban: wireInfo.iban,
        bic: wireInfo.bic,
        accountHolder: wireInfo.accountHolder,
        reference: undefined // TODO esb-452: Add reference field to form state
      };
    },

    /**
     * Helper method to convert a form metering point to the shared type format
     * TODO esb-452: After field renaming and type changes, this can be simplified
     */
    convertMeteringPoint(mp: FormMeteringPoint): IMeteringPointFormData {
      return {
        meteringPoint: mp.meteringPoint,
        meteringPointDirection: mp.meteringPointDirection as MeteringPointDirection,
        nameFriendly: mp.nameFriendly,
        addressPostal: this.convertAddressPostal(mp.addressPostal),
        generationType: mp.plant.generationType,
        generationConfiguration: mp.plant.generationConfiguration as GenerationConfiguration,
        lineCapacity: mp.plant.lineCapacity ? parseFloat(mp.plant.lineCapacity) : undefined,
        assumptionEnergyPerYearKwh: mp.assumptionEnergyPerYearKwh ? parseFloat(mp.assumptionEnergyPerYearKwh) : undefined,
        energyStorageKwh: mp.energyStorageKwh ? parseFloat(mp.energyStorageKwh) : undefined
      };
    },

    /**
     * Convert to IMemberPersonalFormData format from our state
     * TODO esb-452: After restructuring, this can be simplified
     */
    getPersonalData(): IMemberPersonalFormData {
      const state = this.communityMember;

      return {
        nameFriendly: state.contactData.nameFriendly || `${state.contactData.nameFirst} ${state.contactData.nameLast}`,
        legal: state.legal,
        confirmedSmeState: state.confirmedSmeState || false,
        contactData: this.convertContactData(state.contactData)
      };
    },

    /**
     * Convert to IMemberBillingFormData format from our state
     * TODO esb-452: After restructuring, this can be simplified
     */
    getBillingData(): IMemberBillingFormData {
      const state = this.communityMember;

      return {
        addressPostal: this.convertAddressPostal(state.billingData.addressPostal),
        identification: state.billingData.identification,
        identificationType: state.billingData.identificationType as unknown as IdentificationType,
        stand: state.legal,
        wireInfo: this.convertWireInfo(state.billingData.wireInfo),
        vatId: state.billingData.taxInfo.uid, // TODO esb-452: Rename uid to vatId
        vatExempt: state.billingData.taxInfo.taxSelection === "exempt" // TODO esb-452: Use boolean instead of string
      };
    },

    /**
     * Get formatted data for API calls
     * TODO esb-452: After restructuring, this will be much simpler as the form structure
     * will more closely match the API structure
     */
    getFormDataForApi(): IMemberApplicationFormData {
      const state = this.communityMember;

      return {
        agreedJoin: state.agreedJoin,
        agreedDataProcessing: state.agreedDataProcessing,
        agreedNewsletterConsent: state.agreedNewsletterConsent,
        agreedSepa: state.agreedSepa,
        notes: state.notes,
        personalData: this.getPersonalData(),
        billingData: this.getBillingData(),
        meteringPoints: state.meteringPointsAssociated
          .filter(mp => mp.meteringPoint) // Only include ones with a meteringPoint value
          .map(mp => this.convertMeteringPoint(mp))
      };
    },

    addMeteringPoint() {
      this.communityMember.meteringPointsAssociated.push({
        nameFriendly: "",
        meteringPoint: "",
        meteringPointDirection: "",
        ecnDso: "", // Initialize as empty string, will be set by user selection
        participationFactor: 100, // Default participation factor
        addressPostal: {
          name: "",
          address: "",
          addressExtra: "",
          zip: "",
          city: "",
          country: "AT",
        },
        assumptionEnergyPerYearKwh: "",
        energyStorageKwh: "",
        plant: {
          generationConfiguration: "",
          lineCapacity: "",
          generationType: "",
        },
      });
    },

    removeMeteringPoint(index: number) {
      if (index >= 0 && index < this.communityMember.meteringPointsAssociated.length) {
        this.communityMember.meteringPointsAssociated.splice(index, 1);
      }
    }
  }, // actions
});
